/**
 * Il caricamento dell'MRP — il fabbisogno da cui si costruisce il piano.
 *
 * Un file solo, a differenza del piano MES che ne vuole due. E soprattutto:
 * **non sostituisce niente**. Il caricamento precedente resta, e diventa il
 * termine di paragone del confronto. È la ragione per cui qui non c'è nessun
 * avviso del tipo «sostituisce i dati correnti»: sarebbe falso, e toglierebbe
 * a Giacomo la sola cosa che lo può convincere a caricare spesso.
 */
import { AlertTriangle, FileSpreadsheet, Loader2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useM2CaricaMrp } from "@/hooks/useM2";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const ESTENSIONI = ".xls,.xlsx";

export function CaricaMrpDialog({
  aperto,
  onChiudi,
}: {
  aperto: boolean;
  onChiudi: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [sopra, setSopra] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const carica = useM2CaricaMrp();

  function chiudi() {
    setFile(null);
    carica.reset();
    onChiudi();
  }

  // Il messaggio dell'API è già scritto per essere letto da chi carica (dice
  // cosa non va e che il caricamento precedente non è stato toccato): si mostra
  // quello, non una frase generica che lo nasconderebbe.
  const errore =
    carica.error instanceof ApiError
      ? carica.error.message
      : carica.error
        ? String(carica.error)
        : null;

  return (
    <Dialog open={aperto} onOpenChange={(v) => !v && chiudi()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Carica il fabbisogno (MRP)</DialogTitle>
          <DialogDescription>
            L&apos;export del gestionale, così come lo generi oggi. Si aggiunge ai precedenti:
            nessun caricamento viene sostituito, e subito dopo puoi vedere cosa è cambiato.
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            // Senza dichiarare il dropEffect il browser rifiuta il rilascio in
            // silenzio — lezione del calendario, #39.
            e.dataTransfer.dropEffect = "copy";
            setSopra(true);
          }}
          onDragLeave={() => setSopra(false)}
          onDrop={(e) => {
            e.preventDefault();
            setSopra(false);
            const f = e.dataTransfer.files?.[0];
            if (f) setFile(f);
          }}
          className={cn(
            "rounded-lg border border-dashed p-4 transition-colors",
            sopra ? "border-brand-600 bg-brand-50 dark:bg-brand-950/30" : "bg-muted/40"
          )}
        >
          <div className="text-[12.5px] font-medium">File MRP</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Excel (.xls o .xlsx) con le colonne del gestionale
          </div>
          {file ? (
            <div className="mt-2 flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5">
              <FileSpreadsheet className="size-3.5 shrink-0 text-brand-700" />
              <span className="min-w-0 flex-1 truncate text-[12px]">{file.name}</span>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={`Togli ${file.name}`}
              >
                <X className="size-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => input.current?.click()}
              className="mt-2 w-full rounded-md border bg-card px-3 py-1.5 text-[12px] font-medium hover:bg-accent/40"
            >
              Scegli il file o trascinalo qui
            </button>
          )}
          <input
            ref={input}
            type="file"
            accept={ESTENSIONI}
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>

        {errore && (
          <div className="flex gap-2.5 rounded-lg border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <div>{errore}</div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={chiudi}
            className="rounded-md border bg-card px-3.5 py-2 text-sm font-medium hover:bg-accent/40"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={!file || carica.isPending}
            onClick={() => file && carica.mutate(file, { onSuccess: chiudi })}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {carica.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Upload className="size-3.5" />
            )}
            Carica
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
